import { inject, Injectable } from '@angular/core';
import { RestClientWrapper } from '../../market/rest-client-factory';
import { forkJoin, from, map, merge, mergeMap, Observable, switchMap } from 'rxjs';
import { BybitHistoryProvider } from './exchange/bybit-provider';
import { BinanceUsdmHistoryProvider } from './exchange/binance-provider';
import { OkxHistoryProvider } from './exchange/okx-provider';
import { HistoryData } from './model';
import { Funding } from '../../model/trade';
import { InstrumentStorageService } from '../instrument-storage.service';

@Injectable({
  providedIn: 'root'
})
export class RestoreHistoryService {

  private instrumentStorage = inject(InstrumentStorageService)

  restore(rest: RestClientWrapper): Observable<HistoryData> {
    if (rest.isBybit) {
      return this.restoreBybit(new BybitHistoryProvider(rest.bybit))
    }
    if (rest.isBinanceUsdm) {
      return this.restoreBinanceUsdm(new BinanceUsdmHistoryProvider(rest.binanceUsdm))
    }
    if (rest.isOkx) {
      return this.restoreOkx(new OkxHistoryProvider(rest.okx, this.instrumentStorage))
    }
    throw new Error('RestClient type error')
  }

  private restoreBybit(pr: BybitHistoryProvider) {
    return merge(
      pr.requestDeals({}).pipe(
        map(data => {
          const result: HistoryData = {
            deals: data.deals,
            fundings: data.fundings,
          }
          return result
        })
      ),
      pr.requestOrders().pipe(
        map(data => {
          const result: HistoryData = {
            orders: data
          }
          return result
        })
      )
    )
  }

  private restoreBinanceUsdm(pr: BinanceUsdmHistoryProvider) {
    let fundings: Funding[] = []
    return pr.requestIncomes().pipe(
      map(data => {
        const symbols = new Set<string>()
        data.forEach(row => {
          if (row.symbol) {
            symbols.add(row.symbol)
            if (row.incomeType == 'FUNDING_FEE') {
              const funding: Funding = {
                timestamp: row.time,
                market: 'binance',
                board: 'usdm',
                symbol: row.symbol,
                fee: row.income,
              }
              fundings.push(funding)
            }
          }
        })
        return Array.from(symbols)
      }),
      switchMap(symbols => from(symbols)),
      mergeMap(symbol => forkJoin([pr.requestDeals({symbol}), pr.requestOrders({symbol})]), 4),
      map(([deals, orders]) => {
        const data: HistoryData = {deals, orders}
        if (fundings.length) {
          data.fundings = fundings
          fundings = []
        }
        return data
      })
    )
  }

  private restoreOkx(pr: OkxHistoryProvider) {
    return merge(
      pr.requestDeals({}).pipe(
        map(data => {
          const result: HistoryData = {
            deals: data.deals,
            fundings: data.fundings,
          }
          return result
        })
      ),
      pr.requestOrders().pipe(
        map(data => {
          const result: HistoryData = {
            orders: data
          }
          return result
        })
      )
    )
  }

}
